import { useEffect, useMemo, useState } from "react";
import { api, SIZES, koliPairs, koliCount } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import KoliTypeSelect from "@/components/KoliTypeSelect";
import { Boxes, Search } from "lucide-react";

export default function Stock() {
  const { products, koliTypes } = useData();
  const [koliTypeId, setKoliTypeId] = useState("");
  const koliType = koliTypes.find((k) => k.id === koliTypeId) || koliTypes[0];
  const ratios = koliType?.ratios;
  const KOLI_PAIRS = koliPairs(ratios);
  const [stock, setStock] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stock").then((r) => setStock(r.data)).finally(() => setLoading(false));
  }, []);

  const matrix = useMemo(() => {
    let rows = products.map((p) => {
      const sizes = {};
      let total = 0;
      for (const s of stock) {
        if (s.product_id === p.id) { sizes[s.size] = s.qty; total += s.qty; }
      }
      return { ...p, sizes, total, koli: koliCount(sizes, ratios) };
    });
    if (q.trim()) {
      const n = q.toLowerCase();
      rows = rows.filter(r => [r.model_code, r.model_color, r.sole_code, r.sole_color, r.season]
        .some(v => (v || "").toLowerCase().includes(n)));
    }
    return rows.sort((a, b) => b.total - a.total);
  }, [products, stock, q, ratios]);

  const totalPairs = matrix.reduce((a, b) => a + b.total, 0);
  const totalKoli = matrix.reduce((a, b) => a + b.koli, 0);

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="stock-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Hazır Stok Matrisi</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">Stok Durumu</h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Stok kartı bazında numara kırılımlı canlı stok</p>
        </div>
        <div className="rounded-md border border-[#DCCFB8] bg-[#FFFBF4] px-4 py-2 flex items-center gap-3">
          <Boxes className="w-4 h-4 text-[#B45309]" />
          <div>
            <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Toplam</p>
            <p className="font-mono text-2xl font-bold tabular-nums" data-testid="stock-total">{totalPairs}</p>
          </div>
          <div className="border-l border-[#DCCFB8] pl-3">
            <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Koli</p>
            <p className="font-mono text-2xl font-bold tabular-nums text-[#B45309]" data-testid="stock-total-koli">{totalKoli}</p>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C7B68]" />
          <Input data-testid="stock-search" placeholder="Model kodu, renk, taban ara..." value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
        </div>
        <div className="sm:w-[420px]">
          <KoliTypeSelect value={koliType?.id || ""} onChange={setKoliTypeId} testId="stock-koli-type" className="h-11 bg-[#EFE6D6]" />
        </div>
      </div>

      <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] overflow-x-auto" data-testid="stock-matrix">
        <table className="w-full text-sm zs-table">
          <thead className="bg-[#EFE6D6] border-b border-[#DCCFB8] sticky top-0">
            <tr>
              <th className="text-left px-4 py-3">Model Kodu</th>
              <th className="text-left px-4 py-3">Renk</th>
              <th className="text-left px-4 py-3">Taban</th>
              {SIZES.map(s => <th key={s} className="text-center px-2 py-3 w-14">N°{s}</th>)}
              <th className="text-right px-4 py-3">Toplam</th>
              <th className="text-right px-4 py-3">Koli</th>
              <th className="text-right px-4 py-3">Açık Çift</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={16} className="px-4 py-10 text-center text-[#8C7B68]">Yükleniyor...</td></tr>}
            {!loading && matrix.length === 0 && (
              <tr><td colSpan={16} className="px-4 py-10 text-center text-[#8C7B68]">Henüz stok kartı yok.</td></tr>
            )}
            {matrix.map((row) => (
              <tr key={row.id} className="border-b border-[#E8DDCA] hover:bg-[#EFE6D6]/50" data-testid={`stock-row-${row.id}`}>
                <td className="px-4 py-3 font-mono font-semibold text-[#B45309]">{row.model_code}</td>
                <td className="px-4 py-3">{row.model_color}</td>
                <td className="px-4 py-3 text-[#6B5B4A] text-xs">{row.sole_code ? `${row.sole_code} / ${row.sole_color}` : "—"}</td>
                {SIZES.map(s => {
                  const v = row.sizes[s] || 0;
                  const cls = v === 0 ? "text-[#A8977F]" : v < 5 ? "text-[#EF4444]" : v < 15 ? "text-[#B45309]" : "text-[#047857]";
                  return <td key={s} className={`text-center font-mono tabular-nums ${cls}`}>{v}</td>;
                })}
                <td className="px-4 py-3 text-right font-mono font-bold text-base">{row.total}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-base text-[#B45309]" data-testid={`stock-koli-${row.id}`}>{row.koli}</td>
                <td className="px-4 py-3 text-right font-mono text-[#6B5B4A]" data-testid={`stock-loose-${row.id}`}>{row.total - row.koli * KOLI_PAIRS}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
